import { useQuery, useQueryClient, UseQueryResult, keepPreviousData } from '@tanstack/react-query'
import { AxiosError, AxiosResponse, isAxiosError } from 'axios'
import {
    getAreas,
    getFilteredTags,
    getOnboardedSensors,
    getOnboardedSensorsPage,
    getOnboardedSensorsTagsList,
    getPlants,
    getProjects,
    getSuperiorTags,
    getSystems,
    MIN_TAG_SEARCH_LENGTH,
    onboardedSensors,
    OnboardedSensorsPageParams,
    OnboardedSensorsPageResult,
    saveSensorsToDraft,
    searchStidTags,
} from '@/api/apiService'
import { QueryKeys } from '../models/enum'
import {
    InstalledSensorsDataDto,
    PlantDto,
    ProjectDto,
    ResponseObject,
    UseGetReadModel,
    SystemDto,
    AreaDto,
    StidTagsResponseModel,
    SuperiorTagDto,
    OnboardPayload,
    TimeseriesValueDto,
} from '../models/types'
import { Sensor } from '@/features/dashboard/models'
import { normalizeSignalQuality, normalizeBatteryStatus } from '@/core/models/sensorHealth'
import { useEffect, useState } from 'react'
import { useAtomValue } from 'jotai'
import { currentPlantAtom } from '@/core/store/globalStore'

function useGetOnboardedSensorsService(): UseQueryResult<InstalledSensorsDataDto[], AxiosError<ResponseObject>> {
    // Empty short name means "All plants" is selected in the plant selector.
    const plant = useAtomValue(currentPlantAtom)?.shortName ?? ''

    return useQuery({
        queryKey: [QueryKeys.OnboardedSensors, plant],
        queryFn: () => getOnboardedSensors(plant || undefined),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

function useGetOnboardedSensorsTagsListService(
    tag: string[]
): UseQueryResult<InstalledSensorsDataDto[], AxiosError<ResponseObject>> {
    return useQuery({
        queryKey: [QueryKeys.OnboardedSensorsTagsList],
        queryFn: () => getOnboardedSensorsTagsList(tag),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export function mapInstalledSensorToSensor(data: InstalledSensorsDataDto): Sensor {
    return {
        sensorDbKey: data.sensorDbKey,
        plant: data.plant,
        plantId: data.plantId,
        superiorTag: data.superiorTag ?? null,
        area: data.area ?? null,
        tag: data.tag ?? null,
        vendorSerialNumber: data.vendorSerialNumber ?? null,
        sensorModel: data.sensorModel,
        sensorType: data.sensorType ?? null,
        protocol: data.protocol,
        status: data.status,
        installationDate: data.installationDate ? new Date(data.installationDate) : null,
        firstActive: data.firstActive ? new Date(data.firstActive) : null,
        coordinatesNotAvailable: data.coordinatesNotAvailable ? data.coordinatesNotAvailable : false,
        userAddedDescription: data.description ? data.description : null,
        project: data.project ? data.project : null,
        coordinates: data.coordinates ? data.coordinates : null,
        statusMessage: data.statusMessage ? data.statusMessage : null,
        draft: false,
        hasAccess: data.hasAccess ? data.hasAccess : false,
        battery: data.battery ? (data.battery as TimeseriesValueDto) : null,
        batteryStatus: normalizeBatteryStatus(data.batteryStatus),
        batteryEstimated: data.batteryEstimated ?? false,
        rssi: data.rssi ? (data.rssi as TimeseriesValueDto) : null,
        snr: data.snr ? (data.snr as TimeseriesValueDto) : null,
        sf: data.sf ? (data.sf as TimeseriesValueDto) : null,
        signalQuality: normalizeSignalQuality(data.signalQuality),
        lastReceived: data.lastReceived ?? null,
    }
}

export function useGetOnboardedSensors(): UseGetReadModel<Sensor[]> {
    const { isLoading, error, data = [] } = useGetOnboardedSensorsService()
    const transformedData = data.map(mapInstalledSensorToSensor)

    return {
        isLoading,
        data: transformedData,
        error,
        errorMessage: error?.message,
    }
}

function useGetOnboardedSensorsPageService(
    params: OnboardedSensorsPageParams
): UseQueryResult<OnboardedSensorsPageResult, AxiosError<ResponseObject>> {
    return useQuery({
        queryKey: [QueryKeys.OnboardedSensors, 'page', params],
        queryFn: () => getOnboardedSensorsPage(params),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        placeholderData: (previousData, previousQuery) => {
            const previousParams = previousQuery?.queryKey?.[2] as OnboardedSensorsPageParams | undefined
            if (!previousParams) return undefined
            return samePlantContext(previousParams, params) ? previousData : undefined
        },
    })
}

function samePlantContext(a: OnboardedSensorsPageParams, b: OnboardedSensorsPageParams): boolean {
    return (a.plant ?? '') === (b.plant ?? '')
}

export interface UseGetOnboardedSensorsPageResult extends UseGetReadModel<Sensor[]> {
    totalCount: number | undefined
    isFetching: boolean
    refetch: () => void
}

export function usePrefetchOnboardedSensorsPage(params: OnboardedSensorsPageParams | null) {
    const queryClient = useQueryClient()
    useEffect(() => {
        if (!params) return
        void queryClient.prefetchQuery({
            queryKey: [QueryKeys.OnboardedSensors, 'page', params],
            queryFn: () => getOnboardedSensorsPage(params),
        })
    }, [queryClient, params])
}

export function useGetOnboardedSensorsPage(params: OnboardedSensorsPageParams): UseGetOnboardedSensorsPageResult {
    const query = useGetOnboardedSensorsPageService(params)
    const items = query.data?.items ?? []
    const transformedData = items.map(mapInstalledSensorToSensor)
    return {
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        data: transformedData,
        totalCount: query.data?.totalCount,
        error: query.error,
        errorMessage: query.error?.message,
        refetch: () => {
            void query.refetch()
        },
    }
}

export function useGetOnboardedSensorsTagsList(tag: string[]): UseGetReadModel<Sensor[]> {
    const { isLoading, error, data = [] } = useGetOnboardedSensorsTagsListService(tag)

    const transformedData = data.map(mapInstalledSensorToSensor)

    return {
        isLoading,
        data: transformedData,
        error,
        errorMessage: error?.message,
    }
}

export interface SaveToDraftResult {
    success: boolean
    errorMessage?: string
    queuedOffline: boolean
}

// A mutation that never received a response was queued by the service worker and is replayed when back online.
function isOfflineQueuedError(error: unknown): boolean {
    return isAxiosError(error) && !error.response
}

interface UseSaveSensorsToDraft {
    triggerSaveSensorsToDraft: (plant: string, onboardingRequest: OnboardPayload) => Promise<SaveToDraftResult>
    errorDraftMessages: string[]
    isDraftLoading: boolean
    isDraftSuccess: boolean
    isDraftError: boolean
    draftSuccessMessage: string
}

interface UseOnboardSensors {
    triggerOnboardSensors: (
        plant: string,
        onboardingRequest: OnboardPayload
    ) => Promise<void | AxiosResponse<ResponseObject>>
    errorMessages: string[]
    isLoading: boolean
    isSuccess: boolean
    isError: boolean
    isQueuedOffline: boolean
    successMessage: string
}

export function useOnboardSensors(): UseOnboardSensors {
    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [isError, setIsError] = useState(false)
    const [isQueuedOffline, setIsQueuedOffline] = useState(false)
    const [successMessage, setSuccessMessage] = useState<string>('')

    async function triggerOnboardSensors(
        plant: string,
        onboardingRequest: OnboardPayload
    ): Promise<void | AxiosResponse<ResponseObject>> {
        setIsLoading(true)
        setIsSuccess(false)
        setIsError(false)
        setIsQueuedOffline(false)
        setErrorMessages([])
        setSuccessMessage('')

        try {
            const response = await onboardedSensors(plant, onboardingRequest)

            const sensor = response.metadata?.sensor

            setErrorMessages([])

            if (response.success && sensor) {
                if (sensor.success) {
                    setIsSuccess(true)
                    setIsError(false)
                } else {
                    const failedMessage =
                        sensor.message && sensor.message.trim().length > 0 ? sensor.message : 'Sensor failed to save.'
                    setErrorMessages([failedMessage])
                    setIsError(true)
                    setIsSuccess(false)
                }
            } else {
                setErrorMessages(['Unknown error saving.'])
                setIsError(true)
                setIsSuccess(false)
            }
        } catch (error: any) {
            if (isOfflineQueuedError(error)) {
                setIsQueuedOffline(true)
                return
            }
            let message = error?.message ?? 'Unknown error'
            if (error?.response?.status === 403) {
                message = `You do not have access to ${plant}`
            }
            setErrorMessages([message])
            setIsError(true)
            return
        } finally {
            setIsLoading(false)
        }
    }

    return {
        triggerOnboardSensors,
        errorMessages,
        isLoading,
        isSuccess,
        isError,
        isQueuedOffline,
        successMessage,
    }
}

export function useSaveSensorsToDraft(): UseSaveSensorsToDraft {
    const [errorDraftMessages, setErrorMessages] = useState<string[]>([])
    const [isDraftLoading, setIsLoading] = useState(false)
    const [isDraftSuccess, setIsSuccess] = useState(false)
    const [isDraftError, setIsError] = useState(false)
    const [draftSuccessMessage, setSuccessMessage] = useState<string>('')

    async function triggerSaveSensorsToDraft(
        plant: string,
        onboardingRequest: OnboardPayload
    ): Promise<SaveToDraftResult> {
        setIsLoading(true)
        setIsSuccess(false)
        setIsError(false)
        setErrorMessages([])

        try {
            setIsError(false)
            const response = await saveSensorsToDraft(plant, onboardingRequest)
            if (response.success && response.metadata?.sensor) {
                const sensorId = response.metadata.sensor.sensorDbKey
                setSuccessMessage((sensorId || '1 sensor') + ' added to drafts.')
            } else {
                setSuccessMessage('Unknown error saving to drafts.')
            }
            setIsSuccess(true)
            return { success: true, queuedOffline: false }
        } catch (error: any) {
            if (isOfflineQueuedError(error)) {
                setIsSuccess(true)
                return { success: true, queuedOffline: true }
            }
            let message = error?.message ?? 'Unknown error'
            if (error?.response?.status === 403) {
                message = `You do not have access to ${plant}`
            }
            setErrorMessages([message])
            setIsError(true)
            return { success: false, errorMessage: message, queuedOffline: false }
        } finally {
            setIsLoading(false)
        }
    }

    return {
        triggerSaveSensorsToDraft,
        errorDraftMessages,
        isDraftLoading,
        isDraftSuccess,
        isDraftError,
        draftSuccessMessage,
    }
}

function useGetPlantsService(): UseQueryResult<PlantDto[], AxiosError<ResponseObject>> {
    return useQuery({
        queryKey: [QueryKeys.Plants],
        queryFn: getPlants,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export function useGetPlants(): UseGetReadModel<PlantDto[]> {
    const { isLoading, error, data = [] } = useGetPlantsService()

    return {
        isLoading,
        data: data,
        error,
        errorMessage: error?.message,
    }
}

export function useGetProjectsService(plant: string): UseQueryResult<ProjectDto[], AxiosError<ResponseObject>> {
    return useQuery({
        queryKey: [QueryKeys.Projects, plant],
        queryFn: () => getProjects(plant),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export function useGetSystemsService(plant: string): UseQueryResult<SystemDto[], AxiosError<ResponseObject>> {
    return useQuery({
        queryKey: [QueryKeys.Systems, plant],
        queryFn: () => getSystems(plant),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export function useGetAreasService(plant: string): UseQueryResult<AreaDto[], AxiosError<ResponseObject>> {
    return useQuery({
        queryKey: [QueryKeys.Areas, plant],
        queryFn: () => getAreas(plant),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export function useGetSuperiorTagsService(plant: string): UseQueryResult<SuperiorTagDto[], AxiosError<ResponseObject>> {
    return useQuery({
        queryKey: [QueryKeys.SuperiorTags, plant],
        queryFn: () => getSuperiorTags(plant),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export function useStrategyByPlant(plant: string): ProjectDto[] {
    if (plant === '') return []
    const { data } = useGetProjectsService(plant)

    return data
        ? {
              ...data,
          }
        : ({} as ProjectDto[])
}

export function useGetFilteredTagsService(
    plant: string,
    project: string,
    device: string,
    system: string,
    area: string
): UseQueryResult<StidTagsResponseModel[], AxiosError<ResponseObject>> {
    return useQuery({
        queryKey: [QueryKeys.FilteredTags, plant, project, device, system, area],
        queryFn: () => getFilteredTags(plant, project, device, system, area),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

// `search` is expected to be debounced by the caller; react-query then handles caching and out-of-order responses.
export function useSearchStidTagsService(
    plant: string,
    search: string
): UseQueryResult<StidTagsResponseModel[], AxiosError<ResponseObject>> {
    return useQuery({
        queryKey: [QueryKeys.StidTagSearch, plant, search.trim()],
        queryFn: () => searchStidTags(plant, search),
        enabled: plant !== '' && search.trim().length >= MIN_TAG_SEARCH_LENGTH,
        placeholderData: keepPreviousData,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}
